import React, { useState, useEffect } from 'react';

import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { FormTextField, FormDateField, FormCpfField, FormCnpjField } from 'components/forms/FormikFields';
import { Formik, Form } from 'formik';
import moment from 'moment';

import CustomerService from 'services/CustomerService';
import PhoneUtils from 'commons/utils/phoneUtils';
import MaskUtils from 'commons/utils/maskUtils';

import yup from 'commons/validations';
import ConfirmationDialog from 'components/modals/ConfirmationDialog';
import FormTextFieldMaskedPhone from 'components/forms/inputs/FormTextFieldMaskedPhone';
import { getLocale } from 'commons/locale/localeSettings';
import { LOCALE_PT_PT } from 'commons/locale/localeConstants';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  actionsRoot: {
    justifyContent: 'space-between',
  },
  btnShowMore: { textAlign: 'center', marginTop: '10px', textTransform: 'capitalize' },
  flexGrow1: { flex: 1, marginTop: 8 },
  spacer: { width: '22px' },
  radioButton: {
    paddingLeft: '15px',
    paddingRight: '9px',
    paddingBottom: '0px !important',
    paddingTop: '0px !important',
  },

  dateField: { flex: 1, marginTop: 8 },
}));

const schema = yup.object().shape({
  name: yup.string().min(2).max(50).required(),
  phone1: yup.string().max(16),
  phone2: yup.string().max(16).nullable(),
  address: yup.string().max(150).nullable(),
  birthDate: yup.string().nullable().notOneOf(['Invalid date'], 'Data inválida'),
  cpf: yup
    .string()
    .min(11)
    .matches(/\d\d\d\.\d\d\d\.\d\d\d-\d\d/, {
      excludeEmptyString: true,
      message: 'Informe um CPF válido no formato 123.123.123-23',
    })
    .nullable(),
  cnpj: yup
    .string()
    .matches(/\d\d\.\d\d\d\.\d\d\d\/\d\d\d\d-\d\d/, {
      excludeEmptyString: true,
      message: 'Informe um CNPJ válido no formato 23.123.123/0000-23',
    })
    .nullable(),
  comments: yup.string().max(550).nullable(),
  phoneInternational: yup.string().max(25).nullable(),
  email: yup.string().max(50).nullable(),
});

const schemaPortugal = yup.object().shape({
  name: yup.string().min(2).max(50).required(),
  phone1: yup.string().max(16),
  phone2: yup.string().max(16).nullable(),
  address: yup.string().max(150).nullable(),
  birthDate: yup.string().nullable().notOneOf(['Invalid date'], 'Data inválida'),
  cpf: yup.string().min(9).max(14).nullable(),
  comments: yup.string().max(550).nullable(),
  phoneInternational: yup.string().max(25).nullable(),
  email: yup.string().max(50).nullable(),
});

const DEFAULT_VALUES = {
  name: '',
  phone1: '',
  phone2: '',
  birthDate: null,
  address: '',
  cpf: '',
  cnpj: '',
  comments: '',
  phoneInternational: '',
  email: '',
  isCnpj: false,
};

// COMPONENT
function CustomerFormModal({ open, defaultName, onClose, onCreateCallback, customerId }) {
  const { classes } = useStyles();
  const [initialValues, setInitialValues] = useState(DEFAULT_VALUES);
  const [showConfirmOverrideModal, setShowConfirmOverrideModal] = useState({ open: false });
  const [showMoreFields, setShowMoreFields] = useState(false);

  const currentLocale = getLocale();
  const isPortugal = currentLocale === LOCALE_PT_PT;

  useEffect(() => {
    async function initData() {
      if (customerId) {
        CustomerService.getById(customerId).then((resp) => {
          const isCnpj = resp.cnpj != null && resp.cnpj !== '';
          const initVal = {
            ...resp,
            birthDate: resp.birthDate != null ? moment(resp.birthDate, 'YYYY-MM-DD') : null,
            phone1: resp.phone1 ?? '',
            phone2: resp.phone2 ?? '',
            address: resp.address ?? '',
            cpf: resp.cpf ?? '',
            cnpj: resp.cnpj ?? '',
            comments: resp.comments ?? '',
            phoneInternational: resp.phoneInternational ?? '',
            email: resp.email ?? '',
            isCnpj,
          };
          setInitialValues(initVal);
        });
      } else {
        setInitialValues({
          ...DEFAULT_VALUES,
          name: defaultName ?? '',
          timestamp: new Date().getTime(),
        });
      }
    }
    if (open) {
      initData();
    }
  }, [open, defaultName, customerId]);

  const myHandleSubmit = async (values, actions) => {
    const submission = {
      ...values,
      phone1:
        values.phone1 != null && values.phone1 !== '' ? PhoneUtils.sanitizePhoneNumber(values.phone1) : null,
      phone2:
        values.phone2 != null && values.phone2 !== '' ? PhoneUtils.sanitizePhoneNumber(values.phone2) : null,
      birthDate: values.birthDate != null ? values.birthDate.format('YYYY-MM-DD') : null,
      cpf: values.isCnpj ? null : values.cpf,
      cnpj: values.isCnpj ? values.cnpj : null,
    };

    if (submission.phone1 != null) {
      // check if exists
      const justLastPartOfPhone1 = PhoneUtils.cleanUpPhoneNumberAndGetLast8Digits(values.phone1);
      const customerExists = await CustomerService.existsByPhone(justLastPartOfPhone1, values.id);
      if (customerExists != null && customerExists.length > 0) {
        const conflictingCustomer = customerExists[0];
        setShowConfirmOverrideModal({
          open: true,
          conflictingCustomer,
          closeCallback: () => {
            actions.setSubmitting(false);
          },
          confirmCallback: () => {
            CustomerService.save(submission)
              .then((created) => {
                actions.setSubmitting(false);
                onCreateCallback(created);
              })
              .catch(() => {
                actions.setSubmitting(false);
              });
          },
        });
      } else {
        CustomerService.save(submission)
          .then((created) => {
            actions.setSubmitting(false);
            onCreateCallback(created);
          })
          .catch(() => {
            actions.setSubmitting(false);
          });
      }
    } else {
      CustomerService.save(submission)
        .then((created) => {
          actions.setSubmitting(false);
          onCreateCallback(created);
        })
        .catch(() => {
          actions.setSubmitting(false);
        });
    }
  };

  return (
    <Dialog onClose={onClose} aria-labelledby="customized-dialog-title" open={open} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h5" component="span">
          {customerId ? 'Atualizando Cliente' : 'Criando Cliente'}
        </Typography>
      </DialogTitle>

      {/* CONFIRM DIALOG */}
      <ConfirmationDialog
        title="Cliente já existe"
        message={`Já existe um cliente com o nome '${
          showConfirmOverrideModal?.conflictingCustomer?.name
        }' cadastrado com este número '${MaskUtils.phone(
          showConfirmOverrideModal?.conflictingCustomer?.phone1,
        )}'.\nContinuar e criar assim mesmo?`}
        open={showConfirmOverrideModal.open}
        onConfirm={() => {
          setShowConfirmOverrideModal({ open: false });
          showConfirmOverrideModal.confirmCallback();
        }}
        onClose={() => {
          setShowConfirmOverrideModal({ open: false });
          showConfirmOverrideModal.closeCallback();
        }}
      />
      {/* CONFIRM DIALOG */}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isPortugal ? schemaPortugal : schema}
        onSubmit={myHandleSubmit}
      >
        {({ dirty, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Box display="flex" flexDirection="row" alignItems="center">
                <FormTextField fullWidth label="Nome" name="name" />
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <FormTextFieldMaskedPhone fullWidth label="Telefone1" name="phone1" />
                <FormTextFieldMaskedPhone fullWidth label="Telefone2" name="phone2" />
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <div className={classes.dateField}>
                  <FormDateField
                    fullWidth
                    label="Data Nascimento"
                    name="birthDate"
                    format="DD/MM/YYYY"
                    doNotCheckValidity
                  />
                </div>
                <div className={classes.spacer} />
                <div className={classes.flexGrow1}>
                  {isPortugal ? (
                    <FormTextField fullWidth label="NIF" name="cpf" />
                  ) : (
                    <>
                      <RadioGroup
                        row
                        aria-label="position"
                        name="position"
                        defaultValue="top"
                        value={`${values.isCnpj}`}
                        onChange={(evt) => {
                          if (evt.target.value === 'true') {
                            setFieldValue('isCnpj', true);
                            setFieldValue('cpf', '');
                            setFieldValue('cnpj', '');
                          } else {
                            setFieldValue('isCnpj', false);
                            setFieldValue('cpf', '');
                            setFieldValue('cnpj', '');
                          }
                        }}
                      >
                        <FormControlLabel
                          value="false"
                          control={<Radio color="primary" className={classes.radioButton} />}
                          label="CPF"
                        />
                        <FormControlLabel
                          value="true"
                          control={<Radio color="primary" className={classes.radioButton} />}
                          label="CNPJ"
                        />
                      </RadioGroup>
                      {values.isCnpj ? (
                        <FormCnpjField fullWidth name="cnpj" style={{ marginTop: 0 }} />
                      ) : (
                        <FormCpfField fullWidth name="cpf" style={{ marginTop: 0 }} />
                      )}
                    </>
                  )}
                </div>
              </Box>

              <Button
                fullWidth
                size="small"
                color="primary"
                className={classes.btnShowMore}
                onClick={() => {
                  setShowMoreFields(!showMoreFields);
                }}
              >
                {showMoreFields ? 'Menos campos' : 'Mais campos'} (Tel Internacional, Email, Endereço...)
              </Button>

              {showMoreFields && (
                <>
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <FormTextField fullWidth label="Telefone Internacional" name="phoneInternational" />
                  </Box>
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <FormTextField fullWidth label="Email" name="email" type="email" />
                  </Box>
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <FormTextField fullWidth label="Endereço" name="address" />
                  </Box>
                </>
              )}
            </DialogContent>

            <DialogActions
              classes={{
                root: classes.actionsRoot,
              }}
            >
              <Button variant="outlined" onClick={() => onClose()}>
                Fechar
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={(!dirty && !defaultName) || isSubmitting}
              >
                Salvar
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default CustomerFormModal;
